import { z } from 'zod';
import { RoleSchema, UserStatusSchema } from './common.schema.js';

export const PasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[0-9]/, 'Password must contain a digit');

export const LoginRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
  password: z.string().min(1),
  // Optional 6-digit TOTP token. Only required when the user has 2FA enabled
  // — the server returns TOTP_REQUIRED on the first attempt without it, and
  // the client re-submits with the token. Stays optional in the schema so
  // non-2FA users keep their existing one-step flow.
  totpToken: z
    .string()
    .regex(/^\d{6}$/, 'TOTP token must be 6 digits')
    .optional(),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const ForgotPasswordRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(10),
  password: PasswordSchema,
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: PasswordSchema,
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

export const AuthUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  role: RoleSchema,
  status: UserStatusSchema,
  employeeId: z.string().nullable(),
  displayName: z.string().nullable(),
  /**
   * True when the user must rotate their password before they can use the
   * app. Set by bulk-import / admin reset / installer-seeded admin.
   * Frontend route guard reads this from /api/auth/me and the login
   * response, then redirects to /change-password until cleared.
   */
  mustChangePassword: z.boolean().default(false),
  /**
   * Every `<module>.<action>` the person's access role grants, resolved by the
   * server. The console shows and hides by THESE, never by comparing `role`, so
   * what Settings → Roles & access says is what the screens do.
   */
  permissions: z.array(z.string()).optional(),
  accessRoleName: z.string().nullable().optional(),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

export const LoginResponseSchema = z.object({
  data: z.object({
    user: AuthUserSchema,
    accessToken: z.string(),
  }),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/* ===========================================================================
 * VIEW AS — a Super Administrator looks at the console exactly as one person
 * sees it, and changes nothing (owner, 2026-10-20: "a specific person, look
 * only"). The server mints a short access token for that person which cannot
 * be refreshed and refuses every write; the administrator's own session stays
 * in the refresh cookie, so leaving needs no new sign-in.
 * ======================================================================== */
export const ViewAsStartRequestSchema = z.object({ userId: z.string().min(1).max(64) });
export type ViewAsStartRequest = z.infer<typeof ViewAsStartRequestSchema>;

// Zod-free, so the console's API client can read it (../errorCodes.ts).
export { VIEW_AS_READ_ONLY_CODE } from '../errorCodes.js';
/** How long one view-as session lasts. It is never extended. */
export const VIEW_AS_TTL_MINUTES = 30;
